define((require) => {
    "use strict";

    var $            = require("jquery"),
        _            = require("underscore"),
        Class        = require("shared/util/Class"),
        EditorEvents = require("shared/services/EditorEvents"),
        Keyboard     = require("services/Keyboard");

    var MAX_ENTRIES = 10;

    /**
     * The EditorActivityManager keeps track of certain activities inside the
     * editor, providing undo/redo functionality.
     *
     * @class EditorActivityManager
     * @author Kim
     */
    var EditorActivityManager = Class.extend({

        /**
         * The internal state/index.
         *
         * @type {Number}
         */
        _index: 0,

        /**
         * List of activities.
         *
         * @type {Array} Array of <tt>EditorActivity</tt>.
         */
        _entries: [],

        /**
         * The unique id is needed to mark activity as part of batch of activities.
         *
         * @type {String}
         */
        _batchActivityId: null,

        /**
         * Constructor.
         */
        constructor: function() {
            var self = this;

            // Convenience method for creating a shortcut handler that will only invoke the callback if the
            // document's active element does not have native support for undo/redo
            function handler(callback) {
                return function() {
                    // Returning true will perform the default browser behavior
                    return self._hasNativeSupport() ? true : callback.call(self, true);
                };
            }
            Keyboard.Shortcut.bind(["ctrl+z", "meta+z"], handler(this.undo));
            Keyboard.Shortcut.bind(["ctrl+y", "meta+y"], handler(this.redo));
        },

        getUndoRedoButtons() {
            this.undoButton = document.getElementsByClassName("editor-undo")[0];
            this.redoButton = document.getElementsByClassName("editor-redo")[0];
        },

        enableDisableUndoRedoButtons() {
            if (!this.undoButton || !this.redoButton) {
                this.getUndoRedoButtons();
            }

            if (this.undoButton) {
                if (this.canUndo()) {
                    this.undoButton.classList.remove("disabled");
                } else {
                    this.undoButton.classList.add("disabled");
                }
            }

            if (this.redoButton) {
                if (this.canRedo()) {
                    this.redoButton.classList.remove("disabled");
                } else {
                    this.redoButton.classList.add("disabled");
                }
            }
        },

        _hasNativeSupport: function() {
            return $(document.activeElement).is(":input") || window.getSelection().type === "Range";
        },

        /**
         * Detects undo/redo events on Dynamic Product Widgets and performs required steps to
         * ensure the parent widget behaves properly.
         * activity, i.e. any subsequent #undo call will invoke the activity's undo method.
         *
         * @param {EditorActivity} activity The activity to inspect for a Dynamic Product Widget update.
         */
        _updateDynamicProductWidgets: function(activity) {
            const {_widgetModel} = activity;
            if (_widgetModel && _widgetModel.attributes && _widgetModel.attributes.subtype) {
                // Dynamic Product sub widgets have the "subtype" attribute.
                // Triggering the "transformed" is required to adjust the parent widget's selection box.
                activity._widgetModel.trigger("transformed");
            }
        },

        _getWidgetType(activity) {
            const widgetModelType = activity._widgetModel ? activity._widgetModel.get("type") : activity._widgetModels[0].get("type");
            return activity.widgetType || widgetModelType;
        },

        getActivity: function(index) {
            return this._entries[index];
        },

        setBatchActivityId(batchActivityId) {
            this._batchActivityId = batchActivityId;
        },

        /**
         * You cannot undo, if there are no activities yet or if your current state
         * is the oldest activity.
         *
         * @return {Boolean} Returns true, if redo is a doable action
         */
        canUndo: function() {
            return !(this._entries.length === 0 || this._index === this._entries.length);
        },

        /**
         * You cannot redo, if your current state is the latest activity.
         *
         * @return {Boolean} Returns true, if redo is a doable action
         */
        canRedo: function() {
            return this._index !== 0;
        },

        /**
         * Adds/registers an activity. The activity will be added as the most recent
         * activity, i.e. any subsequent #undo call will invoke the activity's undo method.
         *
         * @param {EditorActivity} activity The activity to add.
         */
        add: function(activity) {
            activity._batchActivityId = this._batchActivityId;
            // Add the activity at the current state/index of the list of entries
            this._entries.splice(this._index, 0, activity);

            if (this._entries.length > MAX_ENTRIES) {
                var leastRecentActivity = this._entries.pop();
                leastRecentActivity.dispose();
            }

            this.enableDisableUndoRedoButtons();
        },

        /**
         * Undo the most recent activity.
         * @param {Boolean} keyboard, whether the event was triggered by a keyboard pressure
         * @returns {Boolean} Always false, to prevent default browser behaviour.
         */
        undo: function(keyboard) {
            var activity = this.getActivity(this._index);

            if (activity && !activity.isUndone()) {
                activity.undo();
                this._updateDynamicProductWidgets(activity);
                this._index = Math.min(this._entries.length, this._index + 1);

                var eventProperties = {
                    triggerOrigin: keyboard ? "KeyPress" : "EditorContextRightClickMenu",
                    widgetType: this._getWidgetType(activity),
                    changedAttributes: activity._changedAttributes
                };

                EditorEvents.trigger(EditorEvents.constants.UNDO_ACTIVITY, eventProperties);

                var prevActivity = this.getActivity(this._index);
                if (prevActivity && prevActivity._batchActivityId && prevActivity._batchActivityId === activity._batchActivityId) {
                    this.undo(keyboard);
                }
            }
            this.enableDisableUndoRedoButtons();
            // Prevent default browser behavior
            return false;
        },

        /**
         * Redo what was recently undone.
         * @param {Boolean} keyboard, whether the event was triggered by a keyboard pressure
         * @returns {Boolean} Always false, to prevent default browser behaviour.
         */
        redo: function(keyboard) {
            var index = Math.max(0, this._index - 1),
                activity = this.getActivity(index);

            if (activity && activity.isUndone()) {
                activity.redo();
                this._updateDynamicProductWidgets(activity);
                this._index = index;

                var eventProperties = {
                    triggerOrigin: keyboard ? "KeyPress" : "EditorContextRightClickMenu",
                    widgetType: this._getWidgetType(activity),
                    changedAttributes: activity._changedAttributes
                };

                EditorEvents.trigger(EditorEvents.constants.REDO_ACTIVITY, eventProperties);

                var nextActivity = this.getActivity(this._index - 1);
                if (nextActivity && nextActivity._batchActivityId && nextActivity._batchActivityId === activity._batchActivityId) {
                    this.redo(keyboard);
                }
            }
            this.enableDisableUndoRedoButtons();
            // Prevent default browser behavior
            return false;
        },

        /**
         * Clears all activies. Resetting the state of the activity manager.
         */
        clear: function() {
            _.invoke(this._entries, "dispose");

            this._entries = [];
            this._index = 0;
            this.undoButton = null;
            this.redoButton = null;
        }

    });

    // Singleton
    return new EditorActivityManager();
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/editor/activity/EditorActivityManager.js